/**
 * Invite Table Component
 *
 * Table display for invite list with actions.
 *
 * @fileoverview Invite data table component
 */

import React from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Badge,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  IconButton,
  Box,
  Text,
  Skeleton,
  useColorModeValue,
  Icon,
  Flex,
  Button,
  Tooltip,
} from '@chakra-ui/react';
import {
  MdMoreVert,
  MdVisibility,
  MdRefresh,
  MdDelete,
  MdCancel,
  MdChevronLeft,
  MdChevronRight,
} from 'react-icons/md';
import type { AdminInvite, PaginationState } from '../../types/admin';

interface InviteTableProps {
  invites: AdminInvite[];
  loading: boolean;
  pagination: PaginationState;
  onPageChange: (page: number) => void;
  onView: (invite: AdminInvite) => void;
  onResend: (invite: AdminInvite) => void;
  onRevoke: (invite: AdminInvite) => void;
  onDelete: (invite: AdminInvite) => void;
}

/**
 * Get status badge for invite
 */
function getStatusBadge(invite: AdminInvite) {
  if (invite.used) {
    return { colorScheme: 'green', label: 'Used' };
  }
  if (!invite.status) {
    return { colorScheme: 'red', label: 'Revoked' };
  }
  if (new Date(invite.expiresAt) < new Date()) {
    return { colorScheme: 'orange', label: 'Expired' };
  }
  return { colorScheme: 'blue', label: 'Active' };
}

/**
 * Check if invite can be resent
 */
function canResend(invite: AdminInvite): boolean {
  return !invite.used;
}

/**
 * Check if invite can be revoked
 */
function canRevoke(invite: AdminInvite): boolean {
  return invite.status && !invite.used;
}

/**
 * Invite Table Component
 *
 * Displays invites in a table with actions.
 */
export const InviteTable: React.FC<InviteTableProps> = ({
  invites,
  loading,
  pagination,
  onPageChange,
  onView,
  onResend,
  onRevoke,
  onDelete,
}) => {
  const navigate = useNavigate();
  const bgColor = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.700');
  const hoverBg = useColorModeValue('gray.50', 'gray.700');

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  const formatDateTime = (dateString: string) => {
    return new Date(dateString).toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const handleRowClick = (invite: AdminInvite) => {
    navigate(`/admin/invites/${invite.id}`);
  };

  // Loading skeleton
  if (loading) {
    return (
      <Box bg={bgColor} borderRadius="lg" border="1px" borderColor={borderColor}>
        <Table>
          <Thead>
            <Tr>
              <Th>Email</Th>
              <Th>Tenants</Th>
              <Th>Status</Th>
              <Th>Sent By</Th>
              <Th>Expires</Th>
              <Th>Created</Th>
              <Th>Actions</Th>
            </Tr>
          </Thead>
          <Tbody>
            {[1, 2, 3, 4, 5].map((i) => (
              <Tr key={i}>
                <Td><Skeleton h="20px" /></Td>
                <Td><Skeleton h="20px" /></Td>
                <Td><Skeleton h="20px" w="60px" /></Td>
                <Td><Skeleton h="20px" /></Td>
                <Td><Skeleton h="20px" /></Td>
                <Td><Skeleton h="20px" /></Td>
                <Td><Skeleton h="20px" w="40px" /></Td>
              </Tr>
            ))}
          </Tbody>
        </Table>
      </Box>
    );
  }

  // Empty state
  if (invites.length === 0) {
    return (
      <Box
        bg={bgColor}
        borderRadius="lg"
        border="1px"
        borderColor={borderColor}
        p={8}
        textAlign="center"
      >
        <Text color="gray.500">No invites found</Text>
      </Box>
    );
  }

  return (
    <Box>
      <Box bg={bgColor} borderRadius="lg" border="1px" borderColor={borderColor} overflow="hidden">
        <Table>
          <Thead>
            <Tr>
              <Th>Email</Th>
              <Th>Tenants</Th>
              <Th>Status</Th>
              <Th>Sent By</Th>
              <Th>Expires</Th>
              <Th>Created</Th>
              <Th>Actions</Th>
            </Tr>
          </Thead>
          <Tbody>
            {invites.map((invite) => {
              const status = getStatusBadge(invite);
              return (
                <Tr
                  key={invite.id}
                  _hover={{ bg: hoverBg }}
                  cursor="pointer"
                  onClick={() => handleRowClick(invite)}
                >
                  <Td fontWeight="medium">{invite.email}</Td>
                  <Td>
                    {invite.tenant ? (
                      <Tooltip label={`${invite.tenant.slug} • ${invite.tenantRole || 'USER'}`}>
                        <Text fontSize="sm" fontWeight="medium" cursor="pointer" _hover={{ textDecoration: 'underline' }}>
                          {invite.tenant.name}
                        </Text>
                      </Tooltip>
                    ) : (
                      <Text fontSize="sm" color="gray.500">—</Text>
                    )}
                  </Td>
                  <Td>
                    <Badge
                      colorScheme={status.colorScheme}
                      variant="subtle"
                    >
                      {status.label}
                    </Badge>
                  </Td>
                  <Td>
                    {invite.sentBy ? (
                      <Box>
                        {invite.sentBy.displayName && (
                          <Text fontSize="sm">{invite.sentBy.displayName}</Text>
                        )}
                        <Text fontSize="xs" color="gray.500">
                          {invite.sentBy.email}
                        </Text>
                      </Box>
                    ) : (
                      <Text fontSize="sm" color="gray.400">-</Text>
                    )}
                  </Td>
                  <Td>
                    <Tooltip label={formatDateTime(invite.expiresAt)}>
                      <Text fontSize="sm" color="gray.500">
                        {formatDate(invite.expiresAt)}
                      </Text>
                    </Tooltip>
                  </Td>
                  <Td>
                    <Text fontSize="sm" color="gray.500">
                      {formatDate(invite.createdAt)}
                    </Text>
                  </Td>
                  <Td onClick={(e) => e.stopPropagation()}>
                    <Menu>
                      <MenuButton
                        as={IconButton}
                        icon={<Icon as={MdMoreVert} />}
                        variant="ghost"
                        size="sm"
                      />
                      <MenuList>
                        <MenuItem
                          icon={<Icon as={MdVisibility} />}
                          onClick={() => onView(invite)}
                        >
                          View Details
                        </MenuItem>
                        {canResend(invite) && (
                          <MenuItem
                            icon={<Icon as={MdRefresh} />}
                            onClick={() => onResend(invite)}
                          >
                            Resend Invite
                          </MenuItem>
                        )}
                        {canRevoke(invite) && (
                          <MenuItem
                            icon={<Icon as={MdCancel} />}
                            onClick={() => onRevoke(invite)}
                          >
                            Revoke Invite
                          </MenuItem>
                        )}
                        <MenuItem
                          icon={<Icon as={MdDelete} />}
                          color="red.500"
                          onClick={() => onDelete(invite)}
                        >
                          Delete
                        </MenuItem>
                      </MenuList>
                    </Menu>
                  </Td>
                </Tr>
              );
            })}
          </Tbody>
        </Table>
      </Box>

      {/* Pagination */}
      <Flex justify="space-between" align="center" mt={4}>
        <Text fontSize="sm" color="gray.500">
          Showing {(pagination.page - 1) * pagination.limit + 1} to{' '}
          {Math.min(pagination.page * pagination.limit, pagination.total)} of{' '}
          {pagination.total} invites
        </Text>
        <Flex gap={2}>
          <Button
            size="sm"
            variant="outline"
            leftIcon={<Icon as={MdChevronLeft} />}
            isDisabled={pagination.page <= 1}
            onClick={() => onPageChange(pagination.page - 1)}
          >
            Previous
          </Button>
          <Button
            size="sm"
            variant="outline"
            rightIcon={<Icon as={MdChevronRight} />}
            isDisabled={pagination.page >= pagination.totalPages}
            onClick={() => onPageChange(pagination.page + 1)}
          >
            Next
          </Button>
        </Flex>
      </Flex>
    </Box>
  );
};

export default InviteTable;
